import { type ReactNode } from "react";
import * as RadixDialog from "@radix-ui/react-dialog";
import { Button } from "./Button";

/**
 * A modal dialog.
 *
 * Radix handles the focus trap, the Escape key and the aria wiring, which is
 * most of what makes a modal accessible and is easy to get wrong by hand.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-slate-900/40" />
        <RadixDialog.Content
          className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[min(36rem,calc(100vw-2rem))]
                     -translate-x-1/2 -translate-y-1/2 overflow-auto rounded-lg bg-white p-5 shadow-xl"
        >
          <RadixDialog.Title className="text-lg font-semibold text-slate-900">
            {title}
          </RadixDialog.Title>
          {description && (
            <RadixDialog.Description className="mt-1 text-sm text-slate-600">
              {description}
            </RadixDialog.Description>
          )}

          <div className="mt-4">{children}</div>

          <div className="mt-5 flex justify-end gap-2">
            {footer ?? (
              <RadixDialog.Close asChild>
                <Button variant="secondary">Close</Button>
              </RadixDialog.Close>
            )}
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
